import { Component, OnInit, Input } from '@angular/core';
import * as Highcharts from 'highcharts';
import HC_exporting from 'highcharts/modules/exporting';

@Component({
  selector: 'app-basic-bar-chart',
  templateUrl: './basic-bar-chart.component.html',
  styleUrls: ['./basic-bar-chart.component.scss']
})
export class BasicBarChartComponent implements OnInit {

  @Input() catNames: string[];
  @Input() catFields: string[];
  @Input() series: any[];
  @Input() xlabel: string;
  @Input() ylabel: string;
  @Input() zoomType: Highcharts.OptionsZoomTypeValue;

  @Input() opposite: boolean;
  @Input() percent: boolean;
  @Input() horizontal: boolean;
  @Input() width: string = '100%';
  @Input() height: string = '300px';

  @Input() showLegend: boolean = true;
  showGraph: boolean = true;

  chart: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options;
  chartXLabel: string;

  labelToColor: any = {
    original: 'rgba(227,143,72,0.75)',
    benchmark: 'rgba(205,205,205,0.75)',
    investable: 'rgba(129,186,241,0.75)',
    protected: 'rgba(94,187,75,1)',

    default1: 'rgba(40,106,172,0.75)',
    default2: 'rgba(145,187,225,0.75)',
    default3: 'rgba(180,209,236,0.75)',

    inception: 'rgba(27,69,110,0.75)',
    '10y': 'rgba(40,106,172,0.75)',
    '5y': 'rgba(145,187,225,0.75)',
    '3y': 'rgba(180,209,236,0.75)',

    mean: 'rgba(27,69,110,0.75)',
    rmse: 'rgba(40,106,172,0.75)',
    best_5pct: 'rgba(145,187,225,0.75)',
    worst_5pct: 'rgba(180,209,236,0.75)',

    stability: 'rgba(129,186,241,0.75)',

    exposure: 'rgba(40,106,172,0.75)',
    coefficient: 'rgba(40,106,172,0.75)',
    risk_premium: 'rgba(145,187,225,0.75)',
    return_contribution: 'rgba(180,209,236,0.75)',

    sharpe_ratio: 'rgba(40,106,172,0.75)',
    p_value: 'rgba(145,187,225,0.75)',

    maximum_loss: 'rgba(40,106,172,0.75)',
    maximum_drawdown: 'rgba(145,187,225,0.75)',

    turnover: 'rgba(40,106,172,0.75)',
    annualMax: 'rgba(145,187,225,0.75)',
    pctTurnoverMax: 'rgba(180,209,236,0.75)',

    riskyAsset: 'rgba(27,69,110,0.75)',
    cashAmount: 'rgba(40,106,172,0.75)',
    pctRiskBudget: 'rgba(180,209,236,0.75)',

    unprotected: 'rgba(180,209,236,0.75)',
    defensive: 'rgba(145,187,225,0.75)',
    moderate: 'rgba(40,106,172,0.75)',
    aggressive: 'rgba(27,69,110,0.75)',
    maximum_loss_10: 'rgba(145,187,225,0.75)',
    maximum_loss_20: 'rgba(40,106,172,0.75)',
    maximum_loss_30: 'rgba(27,69,110,0.75)',
    maximum_drawdown_10: 'rgba(145,187,225,0.75)',
    maximum_drawdown_20: 'rgba(40,106,172,0.75)',
    maximum_drawdown_30: 'rgba(27,69,110,0.75)',
    one_year: 'rgba(145,187,225,0.75)',
    three_years: 'rgba(40,106,172,0.75)',

    weight: 'rgba(118,168,255,0.5)',
    risk: 'rgba(255,168,118,0.5)',
  };
  labelToName: any = {
    original: 'Original',
    benchmark: 'Benchmark',
    investable: 'Investable',
    protected: 'Protected',

    inception: 'Inception',
    '10y': '10 Years',
    '5y': '5 Years',
    '3y': '3 Years',

    mean: 'Mean',
    rmse: 'Std Error',
    best_5pct: 'Best 5%',
    worst_5pct: 'Worst 5%',

    stability: 'Stability',

    exposure: 'Exposure',
    coefficient: 'Coefficient',
    risk_premium: 'Risk Premium',
    return_contribution: 'Return Contribution',

    sharpe_ratio: 'Sharpe ratio',
    p_value: 'p_value',

    maximum_loss: 'Max Loss',
    maximum_drawdown: 'Max Drawdown',

    turnover: 'Turnover (accrued)',
    annualMax: 'Annual Max',
    pctTurnoverMax: '% of Turnover Max',

    riskyAsset: 'Risky Asset',
    cashAmount: 'Cash Amount',
    pctRiskBudget: '% of Risk Budget',

    unprotected: 'Unprotected',
    defensive: 'Defensive',
    moderate: 'Moderate',
    aggressive: 'Aggressive',
    maximum_loss_10: 'Max Loss - 10%',
    maximum_loss_20: 'Max Loss - 20%',
    maximum_loss_30: 'Max Loss - 30%',
    maximum_drawdown_10: 'Max Drawdown - 10%',
    maximum_drawdown_20: 'Max Drawdown - 20%',
    maximum_drawdown_30: 'Max Drawdown - 30%',
    one_year: '1 Year',
    three_years: '3 Years',

    weight: 'Weight',
    risk: 'Risk',
  };

  seriesLabelToName: any = {...this.labelToName};

  constructor() { }

  ngOnInit(): void {
    this.percent = this.percent!=undefined;
    this.horizontal = this.horizontal!=undefined;

    this.series.forEach((data) => {
      if (('label' in data) && ('name' in data)) {
        this.seriesLabelToName[data['label']] = data['name'];
      }
    });

    // If categories are not specified, data object keys are used.
    // All the series.values must have the same keys.
    if (!this.catNames && this.catFields) {
      this.catNames = this.catFields;
    } else if (this.catNames && !this.catFields) {
      this.catFields = this.catNames;
    } else if (!this.catNames && !this.catFields) {
      this.series.forEach(
        (data) => {
          if (data && data.values) {
            this.catNames = Object.keys(data.values);
            this.catFields = this.catNames;
          }
        }
      )
    }

    let thisComponent = this;
    this.chartOptions = {
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: true
      },
      navigation: {
        buttonOptions: {
          verticalAlign: 'bottom'
        }
      },
      chart: {
        type: this.horizontal ? 'bar' : 'column',
        zoomType: this.zoomType,
        backgroundColor: 'rgba(255,255,255,0)'
      },
      xAxis: {
          title: {
              text: this.xlabel
          },
          categories: this.catNames,
          opposite: (this.opposite!=undefined),
          labels: {
              style: {
                color: 'grey'
              }
          }
      },
      yAxis: {
          title: {
              text: this.ylabel
          },
          gridLineDashStyle: "Dash",
          gridLineColor: 'rgba(100,100,100,1)',
          labels: {
            formatter: function () {
              return ((thisComponent.percent?100:1)*this.value).toFixed(1) + (thisComponent.percent?' %':'');
            },
            style: {
              color: 'grey'
            }
          }
      },
      tooltip: {
        formatter: function () {
          return '<b>' + this.series.name + '</b><br>' +
                  this.point.category + ': ' + ((thisComponent.percent?100:1)*this.y).toFixed(2) + (thisComponent.percent?' %':'');
        },
        useHTML: true
      },
      plotOptions: {
        series: {
            marker: {
                enabled: false
            },
          borderWidth: 0.5,
          borderColor: 'grey'
        }
      },

      // Define the data points. All series have a dummy year
      // of 1970/71 in order to be compared on the same x axis. Note
      // that in JavaScript, months start at 0 for January, 1 for February etc.
      series: this.series.filter(series=>series.values).map(
        (data) => {
          return {
            name: this.seriesLabelToName[data.label],
            data: this.catFields.map(
              (catField) => {
                return data.values[catField]
              }
            ),
            color: this.labelToColor[data.label],
            type: undefined,
          }
        }
      ),

      responsive: {
          rules: [{
              condition: {
                  maxWidth: 500
              },
              chartOptions: {
                  plotOptions: {
                      series: {
                          marker: {
                              radius: 2.5
                          }
                      }
                  }
              }
          }]
      },
      legend: {
        enabled: this.showLegend,
        itemStyle: {
          color:'grey'
        }
      }
    };

    HC_exporting(Highcharts);

  }

}
